import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import axios from 'axios';

interface RiskAssessmentModalProps {
  onboardingId: string;
  clientName: string;
  onClose: () => void;
  onComplete: (riskProfile: string) => void;
}

interface RiskAssessment {
  assessmentId: string;
  recommendedProfile: string;
  finalProfile?: string;
  riskScore: number;
  aiRationale: string;
}

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: #1e293b;
  border-radius: 16px;
  width: 100%;
  max-width: 800px;
  max-height: 90vh;
  overflow-y: auto;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
`;

const ModalTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 700;
  color: #ffffff;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const AIBadge = styled.span`
  background: linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%);
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 1.5rem;
  cursor: pointer;
  &:hover { color: #ffffff; }
`;

const ModalBody = styled.div`
  padding: 2rem;
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 2rem;
`;

const StepDot = styled.div<{ active: boolean; completed: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => props.completed ? '#22c55e' : props.active ? '#3b82f6' : 'rgba(255, 255, 255, 0.2)'};
  transition: all 0.3s;
`;

const QuestionCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const QuestionText = styled.h3`
  font-size: 1.125rem;
  font-weight: 600;
  color: #ffffff;
  margin: 0 0 1rem 0;
`;

const OptionsGrid = styled.div`
  display: grid;
  gap: 0.75rem;
`;

const OptionButton = styled.button<{ selected: boolean }>`
  display: flex;
  align-items: center;
  gap: 1rem;
  width: 100%;
  padding: 1rem 1.25rem;
  background: ${props => props.selected ? 'rgba(59, 130, 246, 0.2)' : 'rgba(255, 255, 255, 0.02)'};
  border: 1px solid ${props => props.selected ? 'rgba(59, 130, 246, 0.5)' : 'rgba(255, 255, 255, 0.1)'};
  border-radius: 8px;
  color: ${props => props.selected ? '#ffffff' : '#94a3b8'};
  font-size: 0.95rem;
  text-align: left;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${props => props.selected ? 'rgba(59, 130, 246, 0.25)' : 'rgba(255, 255, 255, 0.05)'};
    color: #ffffff;
  }
`;

const RadioCircle = styled.div<{ selected: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid ${props => props.selected ? '#3b82f6' : 'rgba(255, 255, 255, 0.3)'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &::after {
    content: '';
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${props => props.selected ? '#3b82f6' : 'transparent'};
  }
`;

const SliderContainer = styled.div`
  padding: 0.5rem 0;
`;

const SliderValue = styled.div`
  text-align: center;
  font-size: 2rem;
  font-weight: 700;
  color: #3b82f6;
  margin-bottom: 0.5rem;
`;

const Slider = styled.input`
  width: 100%;
  height: 8px;
  border-radius: 4px;
  background: linear-gradient(to right, #22c55e, #eab308, #ef4444);
  -webkit-appearance: none;
  cursor: pointer;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 24px;
    height: 24px;
    border-radius: 50%;
    background: white;
    border: 3px solid #3b82f6;
    cursor: pointer;
  }
`;

const SliderLabels = styled.div`
  display: flex;
  justify-content: space-between;
  color: #64748b;
  font-size: 0.75rem;
  margin-top: 0.5rem;
`;

const NumberInput = styled.input`
  width: 120px;
  padding: 0.75rem 1rem;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  color: #ffffff;
  font-size: 1rem;
  text-align: center;

  &:focus {
    outline: none;
    border-color: #3b82f6;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  margin-top: 2rem;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 0.875rem 2rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s;
  flex: 1;

  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
    color: white;
    border: none;
    &:hover { background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%); }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
  ` : `
    background: rgba(255, 255, 255, 0.1);
    color: #e2e8f0;
    border: 1px solid rgba(255, 255, 255, 0.2);
    &:hover { background: rgba(255, 255, 255, 0.15); }
  `}
`;

const ResultsCard = styled.div`
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%);
  border: 1px solid rgba(59, 130, 246, 0.3);
  border-radius: 16px;
  padding: 2rem;
  text-align: center;
`;

const ProfileBadge = styled.div<{ profile: string }>`
  display: inline-block;
  padding: 0.75rem 2rem;
  border-radius: 9999px;
  font-size: 1.25rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 1rem;

  ${props => {
    switch (props.profile?.toLowerCase().replace(/_/g, ' ')) {
      case 'conservative':
        return 'background: rgba(34, 197, 94, 0.2); color: #22c55e; border: 2px solid rgba(34, 197, 94, 0.5);';
      case 'moderately conservative':
        return 'background: rgba(132, 204, 22, 0.2); color: #84cc16; border: 2px solid rgba(132, 204, 22, 0.5);';
      case 'moderate':
        return 'background: rgba(234, 179, 8, 0.2); color: #eab308; border: 2px solid rgba(234, 179, 8, 0.5);';
      case 'moderately aggressive':
        return 'background: rgba(249, 115, 22, 0.2); color: #f97316; border: 2px solid rgba(249, 115, 22, 0.5);';
      case 'aggressive':
        return 'background: rgba(239, 68, 68, 0.2); color: #ef4444; border: 2px solid rgba(239, 68, 68, 0.5);';
      default:
        return 'background: rgba(148, 163, 184, 0.2); color: #94a3b8; border: 2px solid rgba(148, 163, 184, 0.5);';
    }
  }}
`;

const RiskScore = styled.div`
  font-size: 3rem;
  font-weight: 800;
  color: #ffffff;
  margin: 1rem 0;
`;

const RationaleBox = styled.div`
  background: rgba(0, 0, 0, 0.3);
  border-radius: 12px;
  padding: 1.5rem;
  text-align: left;
  margin-top: 1.5rem;
`;

const RationaleTitle = styled.h4`
  font-size: 0.875rem;
  font-weight: 600;
  color: #94a3b8;
  margin: 0 0 0.75rem 0;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const RationaleText = styled.p`
  font-size: 0.95rem;
  color: #e2e8f0;
  line-height: 1.6;
  margin: 0;
  white-space: pre-wrap;
`;

const QUESTIONS = [
  {
    id: 'timeHorizon',
    question: 'What is your investment time horizon?',
    options: [
      { value: 'LESS_THAN_3_YEARS', label: 'Less than 3 years' },
      { value: '3_TO_5_YEARS', label: '3 to 5 years' },
      { value: '5_TO_10_YEARS', label: '5 to 10 years' },
      { value: '10_TO_20_YEARS', label: '10 to 20 years' },
      { value: 'MORE_THAN_20_YEARS', label: 'More than 20 years' },
    ]
  },
  {
    id: 'investmentGoal',
    question: 'What is your primary investment goal?',
    options: [
      { value: 'PRESERVE_CAPITAL', label: 'Preserve my capital and minimize losses' },
      { value: 'GENERATE_INCOME', label: 'Generate steady income from my investments' },
      { value: 'BALANCED_GROWTH', label: 'Balanced growth and income' },
      { value: 'MAXIMIZE_GROWTH', label: 'Maximize long-term growth' },
    ]
  },
  {
    id: 'lossReaction',
    question: 'If your portfolio lost 20% in a market downturn, how would you react?',
    options: [
      { value: 'SELL_ALL', label: 'Sell everything to prevent further losses' },
      { value: 'SELL_SOME', label: 'Sell some holdings to reduce risk' },
      { value: 'HOLD', label: 'Hold steady and wait for recovery' },
      { value: 'BUY_MORE', label: 'Buy more at lower prices' },
    ]
  },
  {
    id: 'incomeNeeds',
    question: 'How important is generating income from this portfolio?',
    options: [
      { value: 'ESSENTIAL', label: 'Essential - I depend on it for living expenses' },
      { value: 'IMPORTANT', label: 'Important - Supplements my other income' },
      { value: 'NICE_TO_HAVE', label: 'Nice to have - But not necessary' },
      { value: 'NOT_NEEDED', label: 'Not needed - I can reinvest all returns' },
    ]
  },
  {
    id: 'investmentExperience',
    question: 'How would you describe your investment experience?',
    options: [
      { value: 'NONE', label: 'None - This is new to me' },
      { value: 'LIMITED', label: 'Limited - Some experience with basic investments' },
      { value: 'MODERATE', label: 'Moderate - Familiar with stocks, bonds, and funds' },
      { value: 'EXTENSIVE', label: 'Extensive - Experienced with various asset classes' },
    ]
  },
  {
    id: 'clientAge',
    question: 'What is your current age?',
    type: 'number'
  },
  {
    id: 'portfolioPercentage',
    question: 'What percentage of your total wealth will this portfolio represent?',
    options: [
      { value: 'LESS_THAN_25', label: 'Less than 25%' },
      { value: '25_TO_50', label: '25% to 50%' },
      { value: '50_TO_75', label: '50% to 75%' },
      { value: 'MORE_THAN_75', label: 'More than 75%' },
    ]
  },
  {
    id: 'volatilityComfort',
    question: 'On a scale of 1-10, how comfortable are you with portfolio volatility?',
    type: 'slider'
  },
];

const RiskAssessmentModal: React.FC<RiskAssessmentModalProps> = ({
  onboardingId,
  clientName,
  onClose,
  onComplete
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState<Record<string, any>>({
    volatilityComfort: 5,
    clientAge: 45
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<RiskAssessment | null>(null);

  const currentQuestion = QUESTIONS[currentStep];
  const isLastQuestion = currentStep === QUESTIONS.length - 1;
  const showResults = currentStep >= QUESTIONS.length;

  const handleAnswer = (value: any) => {
    setAnswers({ ...answers, [currentQuestion.id]: value });
  };

  const handleNext = async () => {
    if (isLastQuestion) {
      // Submit assessment
      setLoading(true);
      try {
        const response = await axios.post(`/api/onboarding-ai/${onboardingId}/risk-assessment`, answers);
        setResult(response.data);
        setCurrentStep(currentStep + 1);
      } catch (error) {
        console.error('Error submitting assessment:', error);
        window.alert('Failed to submit assessment. Please try again.');
      } finally {
        setLoading(false);
      }
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleApprove = async () => {
    if (result) {
      onComplete(result.recommendedProfile);
      onClose();
    }
  };

  const formatProfile = (profile: string) => {
    return profile?.replace(/_/g, ' ').toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            Risk Assessment
            <AIBadge>AI Powered</AIBadge>
          </ModalTitle>
          <CloseButton onClick={onClose}>&times;</CloseButton>
        </ModalHeader>

        <ModalBody>
          {!showResults && (
            <>
              <StepIndicator>
                {QUESTIONS.map((_, idx) => (
                  <StepDot
                    key={idx}
                    active={idx === currentStep}
                    completed={idx < currentStep}
                  />
                ))}
              </StepIndicator>

              <div style={{ color: '#64748b', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                Question {currentStep + 1} of {QUESTIONS.length} for {clientName}
              </div>

              <QuestionCard>
                <QuestionText>{currentQuestion.question}</QuestionText>

                {currentQuestion.type === 'slider' ? (
                  <SliderContainer>
                    <SliderValue>{answers[currentQuestion.id] || 5}</SliderValue>
                    <Slider
                      type="range"
                      min="1"
                      max="10"
                      value={answers[currentQuestion.id] || 5}
                      onChange={e => handleAnswer(parseInt(e.target.value))}
                    />
                    <SliderLabels>
                      <span>Very Uncomfortable</span>
                      <span>Very Comfortable</span>
                    </SliderLabels>
                  </SliderContainer>
                ) : currentQuestion.type === 'number' ? (
                  <div style={{ textAlign: 'center' }}>
                    <NumberInput
                      type="number"
                      min="18"
                      max="100"
                      value={answers[currentQuestion.id] || ''}
                      onChange={e => handleAnswer(parseInt(e.target.value) || null)}
                      placeholder="Age"
                    />
                    <div style={{ color: '#64748b', fontSize: '0.75rem', marginTop: '0.5rem' }}>
                      Enter your age in years
                    </div>
                  </div>
                ) : (
                  <OptionsGrid>
                    {currentQuestion.options?.map(option => (
                      <OptionButton
                        key={option.value}
                        selected={answers[currentQuestion.id] === option.value}
                        onClick={() => handleAnswer(option.value)}
                      >
                        <RadioCircle selected={answers[currentQuestion.id] === option.value} />
                        {option.label}
                      </OptionButton>
                    ))}
                  </OptionsGrid>
                )}
              </QuestionCard>

              <ButtonRow>
                <Button
                  variant="secondary"
                  onClick={handleBack}
                  disabled={currentStep === 0}
                >
                  Back
                </Button>
                <Button
                  variant="primary"
                  onClick={handleNext}
                  disabled={!answers[currentQuestion.id] || loading}
                >
                  {loading ? 'Analyzing...' : isLastQuestion ? 'Get Risk Profile' : 'Next'}
                </Button>
              </ButtonRow>
            </>
          )}

          {showResults && result && (
            <ResultsCard>
              <div style={{ color: '#94a3b8', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                AI-Recommended Risk Profile
              </div>
              <ProfileBadge profile={result.recommendedProfile}>
                {formatProfile(result.recommendedProfile)}
              </ProfileBadge>
              <RiskScore>{result.riskScore}<span style={{ fontSize: '1.5rem', color: '#64748b' }}>/100</span></RiskScore>

              <RationaleBox>
                <RationaleTitle>AI Analysis</RationaleTitle>
                <RationaleText>{result.aiRationale}</RationaleText>
              </RationaleBox>

              <ButtonRow>
                <Button variant="secondary" onClick={() => setCurrentStep(0)}>
                  Retake Assessment
                </Button>
                <Button variant="primary" onClick={handleApprove}>
                  Accept & Continue
                </Button>
              </ButtonRow>
            </ResultsCard>
          )}
        </ModalBody>
      </ModalContent>
    </ModalOverlay>
  );
};

export default RiskAssessmentModal;
